/* eslint-disable react/no-unknown-property */
import { FC } from 'react';
import {
  AccordionGroup,
  AccordionItem,
  Tag,
  TextBody,
  TextCaption,
  TextOverline,
} from '@pypestream/design-system';
import {
  groupIntoTurns,
  type ConversationTurn,
  type RunResult,
  type RunTraceResult,
  type RunTranscriptEntry,
  type TraceSpan,
} from '@pypestream/testview';

import type { TraceState } from './use-test-api';
import './run-results.scss';

/**
 * One bot or user utterance, as separate LINES.
 *
 * A reply arrives as several frames joined by `\n` — the bot routinely splits
 * one answer across messages. They are rendered as separate elements rather
 * than one string with `white-space: pre-wrap`, and that is not a style
 * preference:
 *
 * `white-space` INHERITS into a shadow tree, and `ps-text-body` renders a Lit
 * template that indents around its `<slot>`, inside `ps-text`, which indents
 * again. Setting `pre-wrap` on the host therefore renders the templates' own
 * formatting as real blank lines and a left indent — which is what put ~100px
 * of dead space above every line in this pane. Any wrapper whose host is a web
 * component has the same trap.
 */
const Utterance: FC<{ text: string }> = ({ text }) => (
  <div className="c-run-results__lines">
    {text.split('\n').map((line, i) => (
      <TextBody
        // Frames have no id and identical text is legitimate, so the index is
        // the only stable key available here.
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        size="small"
      >
        {line}
      </TextBody>
    ))}
  </div>
);

/** The choices a bot message offered, when it offered any. */
const Options: FC<{ options: RunTranscriptEntry['options'] }> = ({
  options,
}) =>
  options?.length ? (
    <div className="c-run-results__options">
      {options.map((option) => (
        <Tag
          key={option.value}
          size="small"
          newVariant="outlined"
          test-id="ps-run-results-option"
        >
          {option.label}
        </Tag>
      ))}
    </div>
  ) : null;

/**
 * A labelled block: the overline sits tight to what it names, with the space
 * above it instead. Grouping is carried by proximity, not by a box.
 */
const Section: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="c-run-results__section">
    <TextOverline>{label}</TextOverline>
    {children}
  </div>
);

/**
 * A section the design specifies and this build cannot fill.
 *
 * Dashed and unfilled on purpose. A solid block reads as content that failed
 * to load; the point here is that there is no content yet. And it must never
 * render plausible rows — a greyed-out `Classify → Retrieve → Generate` reads
 * as real data that happens to be dim, and those stage names exist nowhere in
 * this product.
 */
const AwaitingSource: FC<{ label: string; needs: string }> = ({
  label,
  needs,
}) => (
  <div className="c-run-results__awaiting" test-id="ps-run-results-awaiting">
    <TextCaption variant="secondary">{label}</TextCaption>
    <TextCaption variant="tertiary">{needs}</TextCaption>
  </div>
);

const formatMs = (ms: number): string =>
  ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;

const statusVariant = (
  status: TraceSpan['status']
): 'default' | 'warning' | 'error' => {
  if (status === 'error') return 'error';
  if (status === 'warning') return 'warning';
  return 'default';
};

/**
 * What to show in place of trace-backed content while a run's trace is
 * unresolved, shared by `PipelineBreakdown` and `ActionNodeLog` since both
 * source from the same fetch — see `use-test-api.ts`'s `TraceState`.
 */
const TraceStatusNote: FC<{
  traceState: TraceState;
  traceMessage: string | null;
  /** What this particular section is waiting for, in `AwaitingSource` terms. */
  label: string;
}> = ({ traceState, traceMessage, label }) => {
  if (traceState === 'unavailable') {
    return (
      <AwaitingSource
        label={label}
        needs={traceMessage ?? 'the trace lookup is not available here'}
      />
    );
  }
  // `idle` covers the brief gap before the fetch is kicked off; in practice
  // it renders for at most one tick, since the run and the fetch start in
  // the same synchronous block — see `use-test-api.ts`.
  return (
    <TextCaption variant="secondary" test-id="ps-run-results-trace-loading">
      Waiting on Langfuse for this run&rsquo;s trace — can take a few seconds.
    </TextCaption>
  );
};

/**
 * One action node's contribution, as a chip: which pipeline step it was and
 * how long it took. The SAME `TraceSpan[]` backs both this (per turn) and
 * `ActionNodeLog` (for the whole run) — see `TraceSpan`'s doc comment.
 */
const PipelineStep: FC<{ span: TraceSpan }> = ({ span }) => (
  <Tag
    size="small"
    newVariant="outlined"
    variant={statusVariant(span.status)}
    title={span.name}
    test-id="ps-run-results-pipeline-step"
  >
    {span.stepLabel} · {formatMs(span.durationMs)}
  </Tag>
);

/**
 * Per turn: which pipeline steps ran and how long each took.
 *
 * Every turn is handed the WHOLE run's spans, not spans scoped to that one
 * turn — correct today only because a run is a single turn (see
 * `run-conversation.ts`'s doc comment on why a prompt is sent as one verbatim
 * message). Splitting spans by turn needs a turn identifier this fetch does
 * not yet carry, and will need revisiting the day a run drives more than one.
 */
const PipelineBreakdown: FC<{
  spans: TraceSpan[];
  traceState: TraceState;
  traceMessage: string | null;
}> = ({ spans, traceState, traceMessage }) => {
  if (traceState === 'ready' && spans.length === 0) {
    return (
      <TextCaption variant="tertiary" test-id="ps-run-results-pipeline-empty">
        No Langfuse trace was found for this run.
      </TextCaption>
    );
  }
  if (traceState !== 'ready') {
    return (
      <TraceStatusNote
        traceState={traceState}
        traceMessage={traceMessage}
        label="Pipeline breakdown"
      />
    );
  }
  return (
    <div className="c-run-results__pipeline">
      {spans.map((span) => (
        <PipelineStep key={span.id} span={span} />
      ))}
    </div>
  );
};

/** One action node's full detail: what it was, how long it took, and — when
 *  the primary observation carried one — the model and a truncated preview
 *  of what it sent and got back. */
const ActionNodeLogItem: FC<{ span: TraceSpan }> = ({ span }) => (
  <AccordionItem test-id="ps-run-results-action-node">
    <div slot="header" className="c-run-results__row">
      <Tag size="small" variant={statusVariant(span.status)}>
        {span.stepLabel}
      </Tag>
      <TextBody size="small" truncate className="c-run-results__row-text">
        {span.name} · {formatMs(span.durationMs)}
      </TextBody>
    </div>

    <div className="c-run-results__detail">
      {span.model && (
        <Section label="Model">
          <TextCaption variant="secondary">{span.model}</TextCaption>
        </Section>
      )}
      {span.payload.input && (
        <Section label="Input">
          <pre className="c-run-results__payload">{span.payload.input}</pre>
        </Section>
      )}
      {span.payload.output && (
        <Section label="Output">
          <pre className="c-run-results__payload">{span.payload.output}</pre>
        </Section>
      )}
    </div>
  </AccordionItem>
);

const ActionNodeLog: FC<{
  spans: TraceSpan[];
  traceState: TraceState;
  traceMessage: string | null;
}> = ({ spans, traceState, traceMessage }) => {
  if (traceState === 'ready' && spans.length === 0) {
    return (
      <TextCaption variant="tertiary">
        No Langfuse trace was found for this run.
      </TextCaption>
    );
  }
  if (traceState !== 'ready') {
    return (
      <TraceStatusNote
        traceState={traceState}
        traceMessage={traceMessage}
        label="Per-node timings and payloads"
      />
    );
  }
  return (
    <AccordionGroup multiple>
      {spans.map((span) => (
        <ActionNodeLogItem key={span.id} span={span} />
      ))}
    </AccordionGroup>
  );
};

/**
 * The verdict chip.
 *
 * The design shows Pass and Fail. Nothing evaluates a reply yet, so the only
 * honest third state is "no verdict" — and it must be visibly NOT a pass,
 * because a green tick nobody earned is worse than an empty column. Hence
 * the neutral `default` tag rather than `success`.
 */
const Verdict: FC<{ answered: boolean }> = ({ answered }) => (
  <Tag
    size="small"
    variant={answered ? 'default' : 'error'}
    test-id="ps-run-results-verdict"
    title={
      answered
        ? 'The solution replied. Nothing evaluates replies yet, so this is not a pass.'
        : 'The run ended before the solution answered this turn.'
    }
  >
    {answered ? 'Replied' : 'No reply'}
  </Tag>
);

const TurnItem: FC<{
  turn: ConversationTurn;
  spans: TraceSpan[];
  traceState: TraceState;
  traceMessage: string | null;
}> = ({ turn, spans, traceState, traceMessage }) => {
  const answered = turn.reply !== null;

  return (
    <AccordionItem test-id="ps-run-results-turn">
      {/* The header slot rather than the `header` string prop: the verdict is
        a component, and the design puts it inside the row. */}
      <div slot="header" className="c-run-results__row">
        <Verdict answered={answered} />
        <TextBody size="small" truncate className="c-run-results__row-text">
          {/* The REPLY, not the prompt: the verdict column judges what the
            solution said, so that is what the collapsed row must show. */}
          Turn {turn.number} · {turn.reply?.text || turn.userText}
        </TextBody>
      </div>

      <div className="c-run-results__detail">
        <Section label="Sent">
          <Utterance text={turn.userText} />
        </Section>

        <Section label="Reply">
          {turn.reply ? (
            <>
              <Utterance text={turn.reply.text} />
              <Options options={turn.reply.options} />
            </>
          ) : (
            <TextBody size="small" variant="tertiary">
              No reply was received.
            </TextBody>
          )}
        </Section>

        <Section label="Pipeline breakdown">
          <PipelineBreakdown
            spans={spans}
            traceState={traceState}
            traceMessage={traceMessage}
          />
        </Section>
      </div>
    </AccordionItem>
  );
};

export interface RunResultsProps {
  result: RunResult;
  /** The channel the run went against, for the section label. */
  channelName?: string;
  traceState: TraceState;
  trace: RunTraceResult | null;
  traceMessage: string | null;
}

/**
 * The right pane after a run: the conversation that happened, in the shape
 * the design specifies.
 *
 * It is evidence, not a verdict. The pipeline breakdown and the action node
 * log are backed by Langfuse traces fetched via `/test/trace` once the run
 * completes (see `use-test-api.ts`); per-turn pass/fail still comes from a
 * test-runner verdict that does not exist yet, and says so where it will sit.
 *
 * Built from hairlines and spacing rather than filled cards: everything here
 * is subordinate to the two things worth finding — which turn failed, and
 * what the solution actually said.
 */
export const RunResults: FC<RunResultsProps> = ({
  result,
  channelName,
  traceState,
  trace,
  traceMessage,
}) => {
  const { opening, turns } = groupIntoTurns(result.transcript);
  const spans = trace?.spans ?? [];

  return (
    <div className="c-run-results" test-id="ps-run-results">
      <div className="c-run-results__header">
        <TextOverline>Run{channelName ? ` · ${channelName}` : ''}</TextOverline>
        <TextCaption variant="tertiary" className="c-run-results__chat-id">
          {result.chatId}
        </TextCaption>
      </div>

      {/* Guarded here too, not only in the runner: this component renders
        whatever transcript it is handed, including one assembled elsewhere,
        and an "Opening message" heading with nothing under it reads as
        content that failed to load. A greeting is frequently a MENU — options
        and no prose — so options alone are content. */}
      {opening && (opening.text || (opening.options?.length ?? 0) > 0) && (
        // Separate from the turns because it is not one: nothing was tested
        // by it, and numbering it would inflate the count the "Turn N" labels
        // report.
        <Section label="Opening message">
          {opening.text && <Utterance text={opening.text} />}
          <Options options={opening.options} />
        </Section>
      )}

      <Section label="Turns">
        <AccordionGroup multiple>
          {turns.map((turn) => (
            <TurnItem
              key={turn.number}
              turn={turn}
              spans={spans}
              traceState={traceState}
              traceMessage={traceMessage}
            />
          ))}
        </AccordionGroup>
      </Section>

      <Section label="Action node log">
        <ActionNodeLog
          spans={spans}
          traceState={traceState}
          traceMessage={traceMessage}
        />
      </Section>
    </div>
  );
};
